import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: '⚡' },
  { path: '/tasks', label: 'Tasks', icon: '📋' },
  { path: '/agents', label: 'Agents', icon: '🤖' },
  { path: '/projects', label: 'Projects', icon: '📁' },
  { path: '/costs', label: 'Costs', icon: '💰' },
];

function Layout({ children }: LayoutProps) {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-mc-bg flex items-center justify-center">
        <div className="text-mc-muted">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-64 bg-mc-card border-r border-mc-border flex flex-col">
        <div className="p-6 border-b border-mc-border">
          <h1 className="text-xl font-bold text-mc-accent">Mission Control</h1>
          <p className="text-sm text-mc-muted mt-1">MarketBrewer Agents</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-mc-accent text-white'
                        : 'text-mc-muted hover:bg-mc-border hover:text-mc-text'
                    }`
                  }
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-mc-border">
          {user && (
            <div className="flex items-center gap-3 mb-3">
              <img
                src={user.picture}
                alt={user.name}
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-mc-muted truncate">{user.email}</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="status-dot status-online"></div>
              <span className="text-xs text-mc-muted">Online</span>
            </div>
            <button
              onClick={logout}
              className="text-xs text-mc-muted hover:text-mc-text transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {children}
      </main>
    </div>
  );
}

export default Layout;
